import "firebase/auth"

import * as firebase from "firebase/app"

import { apiRequest, handleApiError } from "./api"
import {
  currentUserAction,
  safeReadQuery,
  setupServicesUser,
} from "./user-data"

import { USER_PLAN } from "../apollo/queries"
import VERSION from "../version"
import { checkForUpdates } from "./version-handler"
import client from "../apollo/client"
import { createUser } from "../api/user"
import fbConfig from "../../firebase-config"
import log from "../utils/log"
import { navigate } from "gatsby"

if (!!firebase && typeof firebase.initializeApp === "function")
  firebase.initializeApp(fbConfig)

export default firebase

if (typeof window !== "undefined") {
  window.localStorage.removeItem("isAuthPolling")
}

export const loginPath = "/a/login"

/**
 * Redirects a user to the app's main route
 * upon successful login.
 */
export const redirectLoggedIn = () => {
  // navigate("/a/home")
  const redirectHash = window.localStorage.getItem("login-redirect")
  const path = redirectHash ? "/a/" + redirectHash : "/a/journal"
  window.localStorage.removeItem("login-redirect")
  // window.location.href = path
  navigate(path)
}

/**
 * Redirects a logged out user attempting
 * to access protected routes to the provided
 * route (login page).
 */
export const redirectLoggedOut = customRedirect => {
  clearUser()
  if (customRedirect) {
    window.location.href = customRedirect
    // window.location.assign(customRedirect)
  } else {
    navigate("/a/login#so")
  }
}

/**
 * Checks if a user is logged in
 */
export const isLoggedIn = () => {
  return !!window.localStorage.getItem("isLoggedIn")
}

/**
 * Saves the user's details into local storage after
 * successful authentication.
 * @param {String} idToken current user's `idToken`
 * @param {Object} user the current user object
 */
export const cacheUser = (idToken, user) => {
  window.__auth_token__ = idToken
  window.localStorage.setItem("authToken", idToken)
  window.localStorage.setItem("currentUser", JSON.stringify(user))
  //Apollo Client cache
  client.writeData({ data: { isLoggedIn: true } })
}

/**
 * Clears the saved user out of local storage
 */
export const clearUser = () => {
  console.log("Resetting client store", client)
  client.clearStore()
  window.localStorage.removeItem("userDataLoaded")
  window.localStorage.removeItem("authToken")
  window.localStorage.removeItem("upvotyToken")
  window.localStorage.removeItem("validAuthToken")
  window.localStorage.removeItem("currentUser")
  window.localStorage.removeItem("cachedEntries")
  window.localStorage.removeItem("isPollingToken")
  window.localStorage.removeItem("isAuthenticating")
  window.localStorage.removeItem("isIdentified")
  // window.localStorage.removeItem("VERSION")
  window.localStorage.removeItem("apollo-cache-persist")

  //Segment
  window.localStorage.removeItem("ajs_user_id")
  window.localStorage.removeItem("ajs_anonymous_id")
  window.localStorage.removeItem("ajs_group_id")
  window.localStorage.removeItem("ajs_user_traits")

  //TrackJS
  window.localStorage.removeItem("track_js_installed")
  window.localStorage.removeItem("events")
  window.TrackJS = null
}

/**
 * Checks if a user is currently saved in local
 * storage.
 * @returns user idToken || false
 */
export const hasCachedUser = () => {
  if (
    !!window.localStorage.getItem("authToken") &&
    !!window.localStorage.getItem("currentUser")
  ) {
    return window.localStorage.getItem("authToken")
  } else {
    return false
  }
}

/**
 * Gets user from local storage, if valid.
 */
export const getCachedUser = () => {
  if (!hasCachedUser())
    return {
      user: {},
      token: null,
    }
  return {
    user:
      window.authUser || JSON.parse(window.localStorage.getItem("currentUser")),
    token: window.localStorage.getItem("authToken"),
  }
}

/**
 * Retrieves the current application either from Firebase
 * auth, or if not available, the cached user.
 */
export const getCurrentUser = () => {
  let user = !!firebase.auth().currentUser
    ? firebase.auth().currentUser
    : getCachedUser().user

  user = user.user ? user.user : user

  // console.log("getCurrentUser", { user })

  const { userPlan } = safeReadQuery(USER_PLAN, {
    userPlan: "plan-free",
  })
  user.isPremium = !!userPlan && userPlan !== "plan-free"
  user.accountPlan = !!userPlan ? userPlan : "plan-free"

  return user
}

export const setCurrentUser = user => {
  console.log("TCL: setCurrentUser user:")
  Object.keys(user).forEach(key => {
    console.log(`TCL: ${key}`, user[key])
  })
  window.localStorage.setItem("currentUser", JSON.stringify(user))
  window.currentUser = user
  return user
}

/**
 * Retrieves the current user's ID Token (auth token).
 */
export const getUserIdToken = () => {
  return getCachedUser().token
}

/**
 * Generates a new, refreshed id/auth token for the
 * current user and caches it.
 */
export const generateIdToken = async numRetries => {
  let idToken
  if (firebase.auth().currentUser) {
    idToken = await firebase.auth().currentUser.getIdToken(true)
    window.localStorage.setItem("authToken", idToken)
  }
  // else {
  //   log("log", ["generateIdToken failed"])
  //   let retry = numRetries ? numRetries + 1 : 1
  //   if (retry > 10) {
  //     alert("There was an issue and Reflection App needs to restart.")
  //     window.location.reload(true)
  //   }
  //   return generateIdToken(retry)
  // }
  return idToken
}

const cacheValidToken = token => {
  window.localStorage.setItem("validAuthToken", true)
  window.localStorage.setItem("authToken", token)
  window.__auth_token__ = token
}

/**************************************
 * NEW TOKEN VALIDATION STRUCTURE
 *************************************/
export const validateToken = async (token, retryDelay = 5000) => {
  const user = getCurrentUser()
  const actions = currentUserAction()

  if (actions.logged_out) return false

  let response
  response = await validateTokenRetry(token)

  if (!!response.errors) {
    retryDelay = retryDelay + 5000
    log("log", [
      `Token Validation: validateIdToken failed and is retrying in ${retryDelay}ms.`,
    ])
    if (retryDelay > 25000) {
      if (
        !actions.compose_entry &&
        !actions.compose_monthly &&
        !actions.compose_yearly
      ) {
        signOut()
      }
    }
    setTimeout(async () => {
      validateToken(token, retryDelay)
    }, retryDelay)
    return
  }

  // Set new timestamp for successful token validation
  window.localStorage.setItem("isAuthPolling", new Date().getTime())

  log("log", ["Token Validation: Validated Token", { token, response }])

  verifyApplicationUpdate(response)

  setUpvotyUser(response)

  cacheValidToken(response.idToken)

  handleAuthPolling(5)

  return response && !!response.errors ? false : user
}

const validateTokenRetry = async token => {
  let retries = 0

  return new Promise(resolve => {
    const makeRequest = async () => {
      const query = `{currentUser {uid, idToken, update, upvotyToken, premiumStatus, premiumPlan}}`
      const response = await apiRequest("client", query, token)

      const isError = handleApiError(response, false, "validateIdToken")

      if (isError && retries < 4) {
        retries++
        log("log", [
          "Token validation: validateIDToken request failed with error, retrying...",
          response,
        ])
        return makeRequest()
      }

      if (isError) {
        log("log", [
          "Token validation: validateIdToken failed after 5 retries",
          response,
        ])
        resolve({ errors: response.errors })
      } else {
        if (response && response.data && response.data.currentUser) {
          resolve(response.data.currentUser)
        } else {
          resolve({
            errors: [
              {
                message: "response.data.currentUser is undefined",
                code: "001",
              },
            ],
          })
        }
      }
    }

    makeRequest()
  })
}

const handleAuthPolling = async delay => {
  const isPolling = !!window.localStorage.getItem("isAuthPolling")
  if (!isPolling) {
    // log("log", ["Token Validation: Beginning Auth Polling...", ""])
    window.localStorage.setItem("isAuthPolling", new Date().getTime())
    // Begin polling to refresh Firbase ID token
    const polling = setInterval(async () => {
      const actions = currentUserAction()
      const idToken = hasCachedUser()
      const response = await validateToken(idToken)
      log("log", [
        "Auth Polling Cycle Response",
        { response, idToken, actions },
      ])
      // If fails, stop polling
      if (!response && actions.logged_out) {
        log("log", ["Clearing Auth Polling", "User is logged out"])
        clearInterval(polling)
      }
    }, delay * 60 * 1000)
  }
}

const setUpvotyUser = currentUser => {
  log("log", ["Logging user into Upvoty", currentUser])
  let ssoToken
  if (currentUser) {
    ssoToken = currentUser.upvotyToken
    window.localStorage.setItem("upvotyToken", ssoToken)
    log("log", ["Upvoty SSO token set to:", ssoToken])
  } else {
    log("warn", [
      "AppUpdate: the setUpvotyUser response did not have a proper token in response",
      "",
    ])
  }
  // If response has proper update version number, run check
  // if (ssoToken)
  //   window.upvoty &&
  //     window.upvoty.init("render", {
  //       ssoToken,
  //       baseUrl: "reflection.upvoty.com",
  //     })
}

/**
 * Examines the validateIdToken response for an `update` prop
 * set, with a latest update version number, and if set runs
 * the check for an update.
 * @param {Object} validateTokenResponse Response from validateIdToken
 */
const verifyApplicationUpdate = currentUser => {
  let update
  if (currentUser) update = currentUser.update
  else
    log("warn", [
      "AppUpdate: the validateIdToken response did not have a proper version number attached",
      "",
    ])
  // If response has proper update version number, run check
  if (update) checkForUpdates(update || "0.0.0")
}

/**
 * Checks if the current user is logged in
 * by verifying their token.
 */
export const checkIsLogged = async () => {
  const idToken = hasCachedUser()
  if (!idToken) {
    clearUser()
    return false
  } else {
    let response
    // Prevent repeat token validation on multiple consecutive renders of
    // the PrivateRoute component by adding a
    const dontValidateAgainUntil =
      Number(window.localStorage.getItem("isAuthPolling")) + 20000
    if (new Date().getTime() < dontValidateAgainUntil) {
      response = true
    } else {
      response = await validateToken(idToken, 5000)
    }
    // `validateIdToken` returns the valid user or false
    if (response) {
      return response
    } else {
      console.warn("Token was not validated before page reload")
      return true
    }
  }
}

export const authenticatePrivateRoute = async () => {
  //Todo: fix this check
  //!important fix
  return true

  //old
  // checkIsLogged()
  //   .then(user => {
  //     if (!user && window.location.pathname !== loginPath) {
  //       console.warn("authenticatePrivateRoute Condition Failed", {
  //         user,
  //         pathname: window.location.pathname,
  //         loginPath,
  //       })
  //       redirectLoggedOut()
  //       return
  //     }
  //     // Set authed user to global
  //     window.localStorage.setItem("isLoggedIn", true)
  //     window.authUser = firebase.auth().currentUser
  //   })
  //   .catch(err => {
  //     console.warn("authenticatePrivateRoute Error Failed", err)
  //     redirectLoggedOut()
  //   })
}

export const authenticateUser = async () => {
  console.log("TCL: authenticateUser -> authenticateUser", authenticateUser)
  // Set flag to let us check if coming from auth redirect
  // because firebase check takes up to 2 seconds.
  window.localStorage.setItem("isAuthenticating", true)

  let provider = new firebase.auth.GoogleAuthProvider()
  firebase.auth().signInWithRedirect(provider)
}

const handleSuccessfulAuth = user => {
  // Set authed user to global
  window.authUser = firebase.auth().currentUser

  // Set user to global for tracking purposes
  setCurrentUser(window.authUser)

  // Setup user for  Logging
  setupServicesUser(window.authUser)

  // Get the authenticated user's ID Token (not the same as accessToken above!)
  if (firebase.auth().currentUser) {
    firebase
      .auth()
      // Pass true to force refresh
      .currentUser.getIdToken(false)
      .then(async function(idToken) {
        // Save ID token for future requests
        cacheUser(idToken, user)
        // Create/update user in Firestore
        await createUser(idToken)
        // Redirect after success login
        redirectLoggedIn()
      })
      .catch(function(error) {
        // Handle error
        log("error", ["firebase.auth().currentUser.getIdToken() Error", error])
      })
  } else {
    log("log", ["Auth: firebase auth currentUser is null", ""])
    window.location.reload(true)
  }
}

export const handleAuthenticatedRedirect = () => {
  if (window.localStorage.getItem("isAuthenticating")) {
    // Handle flag for redirect from auth
    navigate("/a/login#redirect")
    window.localStorage.removeItem("isAuthenticating")
    return
  }

  window.Beacon &&
    window.Beacon("identify", {
      version: VERSION.number,
    })

  // Handle login page timeout
  setTimeout(() => {
    if (window.location.href.includes("a/login#redirect")) {
      window.TrackJS &&
        window.TrackJS.track(`User encountered login redirect timeout`)
      alert(
        "Logging in is taking longer than usual.  You will now be redirected back to the login page.  Please try again, and if the problem persists, contact support (blue help button at bottom right)"
      )
      signOut("/a/login")
    }
  }, 30000)

  // Handle redirect from Google authentication
  firebase
    .auth()
    .getRedirectResult()
    .then(function(result) {
      if (result.credential) {
        log("log", ["handleAuthenticatedRedirect result", result])
        let user = result.user
        handleSuccessfulAuth(user)
      }
    })
    .catch(function(error) {
      log("error", ["firebase.auth().getRedirectResult() Error", error])
    })
}

let authToken
if (typeof window !== "undefined") {
  authToken = window.localStorage.getItem("authToken")
}
export const authContext = () => {
  let headers
  if (typeof window !== "undefined") {
    headers = {
      headers: {
        authorization: `Bearer ${window.__auth_token__ ||
          window.localStorage.getItem("authToken")}`,
      },
    }
  } else {
    headers = {
      headers: {
        authorization: `Bearer ${authToken}`,
      },
    }
  }

  return headers
}

export const resetUserPassword = email => {
  if (!email) {
    alert("Please enter your password into the form to continue.")
    return
  }
  firebase
    .auth()
    .sendPasswordResetEmail(email)
    .then(user => {
      alert(
        "Instructions for resetting your password have been sent to your email."
      )
    })
    .catch(error => {
      console.log("TCL: Authentication error", { error })
      switch (error.code) {
        case "auth/email-already-in-use":
          alert(
            "This email belongs to a current user.  If that is you, please trying logging in instead."
          )
          break
        case "auth/invalid-email":
          alert(
            "The email you entered is invalid.  Please check the email is in the proper format and try again"
          )
          break
        case "auth/user-not-found":
          alert(
            "A user with this email does not exist.  Please check the email and try again."
          )
          break
        default:
          alert("An error occurred.  Please check the form and try again.")
      }
    })
}

export const authenticateUsernamePassword = (email, password) => {
  if (!email) {
    alert("Please enter your email into the form to continue.")
    return
  }
  if (!password) {
    alert("Please enter your password into the form to continue.")
    return
  }
  return firebase
    .auth()
    .signInWithEmailAndPassword(email, password)
    .then(user => {
      handleSuccessfulAuth(user)
    })
    .catch(error => {
      console.log("TCL: Authentication error", { error })
      switch (error.code) {
        case "auth/email-already-in-use":
          alert(
            "This email belongs to a current user.  If that is you, please trying logging in instead."
          )
          break
        case "auth/invalid-email":
          alert(
            "The email you entered is invalid.  Please check the email is in the proper format and try again"
          )
          break
        case "auth/wrong-password":
          alert("The password you entered is incorrect.")
          break
        case "auth/user-not-found":
          alert(
            "A user with this email does not exist.  Please check the email and try again."
          )
          break
        default:
          alert("An error occurred.  Please check the form and try again.")
      }
    })
}

export const createAccountUsernamePassword = (
  firstName,
  email,
  password,
  confirmPassword
) => {
  if (!firstName) {
    alert("Please enter your first name, so we know what to call you!")
    return
  }
  if (!password) {
    alert("Please enter your password into the form to continue.")
    return
  }
  if (!email) {
    alert("Please enter your password into the form to continue.")
    return
  }
  if (password !== confirmPassword) {
    alert(
      "The passwords do not match.  Please confirm your password is correct."
    )
    return
  }
  firebase
    .auth()
    .createUserWithEmailAndPassword(email, password)
    .then(user => {
      window.localStorage.setItem("startBeaconViewed", 0)
      firebase
        .auth()
        .currentUser.updateProfile({
          displayName: firstName,
        })
        .then(function() {
          // Update successful.
          handleSuccessfulAuth(user)
        })
        .catch(function(error) {
          // An error happened.
          alert(
            "There was an error while logging in, please refresh the page and try again"
          )
          console.log("TCL: Login error", error)
        })
    })
    .catch(error => {
      console.log("TCL: Authentication error", { error })
      switch (error.code) {
        case "auth/email-already-in-use":
          alert(
            "This email belongs to a current user.  If that is you, please trying logging in instead."
          )
          break
        case "auth/invalid-email":
          alert(
            "The email you entered is invalid.  Please check the email is in the proper format and try again"
          )
          break
        case "auth/weak-password":
          alert(
            "Your password is too weak.  It should be at least 6 character, however the strongest passwords have 12 or more characters."
          )
          break
        default:
          alert("An error occurred.  Please check the form and try again.")
      }
    })
}

export const signOut = customRedirect => {
  const handleUser = () => {
    clearUser()
    redirectLoggedOut(customRedirect)
  }

  firebase
    .auth()
    .signOut()
    .then(function() {
      handleUser()
    })
    .catch(function(error) {
      handleUser()
    })
}
